/**
 * Wires the app's server-rendered modal dialogs to the page: the triggers that
 * open them, the content some of them fetch, and light dismiss where the
 * browser does not supply it. Opening and closing itself is
 * `common/dialogs/dialog`, which feature code calls directly.
 *
 * A trigger carries `data-reveal-id="<dialog id>"`. Adding `data-reveal-ajax`
 * loads the dialog's contents first: the value `true` fetches the trigger's own
 * `href`, and any other value is itself the URL — the staff contracts table
 * opens this way from `<tr>` rows, which have no `href` to read.
 *
 * Fetched content is written in with `innerHTML`, so it has to be plain server
 * markup. A `<script>` in it never runs, and a `react_component` in it never
 * mounts: its props travel in a script, and components mount once, at boot.
 * Behaviour delegated from `document` reaches it — the reveal triggers,
 * `data-confirm`, form validation, tooltips — and anything that scans the page
 * once at boot does not. Content holding a script or a mount point is reported
 * to the console, and opens anyway.
 *
 * `apiClient` is not usable here: it treats a 2xx body that is not valid JSON
 * as a failure (ADR 0016), and these responses are HTML fragments.
 *
 * See ADR 0025 for why browsers without `<dialog>` get no modal behaviour
 * rather than a polyfill, and why the React modals stay a separate system.
 */

import { delegate } from 'common/delegate';
import { isDialog, openModal, SUPPORTS_DIALOG } from 'common/dialogs/dialog';

const TRIGGER_SELECTOR = '[data-reveal-id]:not([disabled])';

/**
 * `closedby="any"` closes a dialog on a click outside it. Safari has not
 * shipped it in any stable release, so the same behaviour is supplied by hand
 * wherever `closedBy` is absent from the prototype. Escape needs no
 * equivalent — `showModal()` handles it in every browser that has `<dialog>`
 * at all.
 */
const SUPPORTS_CLOSEDBY =
  SUPPORTS_DIALOG && 'closedBy' in HTMLDialogElement.prototype;

let initialized = false;

export default function initializeReveal(): void {
  if (initialized) return;
  initialized = true;

  // With no `<dialog>` there is nothing to open, and binding nothing is what
  // leaves every trigger its own behaviour: the ajax login link stays a real
  // `/login` navigation because no listener is there to call `preventDefault`.
  if (!SUPPORTS_DIALOG) return;

  delegate(document, 'click', TRIGGER_SELECTOR, handleTriggerClick);
  // `close` does not bubble, so it is caught on the way down instead.
  document.addEventListener('close', handleClose, true);
  if (!SUPPORTS_CLOSEDBY) {
    document.addEventListener('mousedown', recordPress, true);
    document.addEventListener('click', handleLightDismiss);
  }
}

/**
 * A `click` reports the nearest common ancestor of its press and release, so a
 * drag that starts on a field inside the dialog and ends past its edge — the
 * gesture for selecting text and overshooting — arrives with the dialog itself
 * as the target and coordinates outside the box. A press on the dialog's own
 * padding reports the dialog as its target too, exactly like a press on the
 * backdrop, so the target alone cannot place the press. Native light dismiss
 * requires the pointer to go down *and* up outside the box, so the press's
 * target and coordinates are both held to it. Captured, so a handler that
 * stops propagation cannot hide them.
 */
let pressedOn: EventTarget | null = null;
let pressX = 0;
let pressY = 0;

function recordPress(event: MouseEvent): void {
  pressedOn = event.target;
  pressX = event.clientX;
  pressY = event.clientY;
}

function handleTriggerClick(event: MouseEvent, trigger: Element): void {
  // Every trigger is a real element with its own behaviour — the login and
  // register links carry real hrefs — so a click the platform reserves stays
  // with the browser: a modifier or middle click opens the link in a new tab
  // or window, and a click another handler already took is not ours to
  // cancel. `magellan` gates its in-page links the same way.
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(trigger instanceof HTMLElement)) return;

  const id = trigger.dataset.revealId;
  if (!id) return;

  const dialog = document.getElementById(id);
  // A trigger whose dialog is not on this page keeps its own behaviour: the
  // ajax login link is a real `/login` href.
  if (!isDialog(dialog)) return;

  event.preventDefault();

  const ajax = trigger.dataset.revealAjax;
  if (ajax === undefined) {
    openModal(dialog);
    return;
  }

  const url = ajax === 'true' ? trigger.getAttribute('href') : ajax;
  // A trigger that asks for a fetch and names nowhere to fetch from is a
  // mistake in the view, and the two spellings look interchangeable enough to
  // invite it — `reveal_ajax: true` on a `<button>` or a `<tr>` has no `href`
  // to read. Saying so beats opening an empty box over the page.
  if (!url) {
    // eslint-disable-next-line no-console
    console.error('Modal trigger asks for a fetch but names no URL:', trigger);
    return;
  }

  loadInto(dialog, url).catch(error => {
    // eslint-disable-next-line no-console
    console.error(error);
  });
}

/**
 * A trigger click outruns the request it starts, and every response that lands
 * assigns `innerHTML`: an impatient click on a contracts row issues one fetch
 * per click, so a late one replaces what the user is reading and takes the
 * focused field with it, and one landing after a dismissal calls `showModal()`
 * on a dialog the user just closed — `!dialog.open` is true precisely then.
 *
 * Each dialog carries a revision that both a new request and a close advance.
 * A response only applies while it is still the one being waited for.
 */
const revisions = new WeakMap<HTMLDialogElement, number>();

function nextRevision(dialog: HTMLDialogElement): number {
  const revision = (revisions.get(dialog) ?? 0) + 1;
  revisions.set(dialog, revision);
  return revision;
}

/**
 * A fetched fragment outlives the dialog holding it, so two can be resident at
 * once: the login and register forms both come from Devise's `new_user` form
 * and share its field ids, and id lookup then answers with whichever dialog
 * comes first in the document, leaving the other form's inputs unlabelled
 * (VEN-2056). A dialog that was fetched into refills on every open, so its
 * content is only ever stale once closed.
 *
 * The close fade paints the dialog while `[open]` is already gone, so emptying
 * it as the `close` arrives would fade an empty box. The wait covers the
 * transition, and the guard abandons the clear if the dialog is open again by
 * then — that reopen brings its own content.
 */
const fetched = new WeakSet<HTMLDialogElement>();

function clearAfterClose(dialog: HTMLDialogElement): void {
  const durations = getComputedStyle(dialog).transitionDuration.split(',');
  const ms = durations.reduce((longest, value) => {
    const seconds = Number.parseFloat(value);
    return Number.isNaN(seconds) ? longest : Math.max(longest, seconds * 1000);
  }, 0);

  window.setTimeout(() => {
    if (dialog.open) return;
    fetched.delete(dialog);
    dialog.innerHTML = '';
  }, ms);
}

function handleClose(event: Event): void {
  const dialog = event.target;
  if (!isDialog(dialog)) return;
  nextRevision(dialog);
  if (fetched.has(dialog)) clearAfterClose(dialog);
}

async function loadInto(dialog: HTMLDialogElement, url: string): Promise<void> {
  const revision = nextRevision(dialog);
  // Closing a dialog removes its `open` attribute synchronously but delivers
  // `close` — and with it the revision bump — from a queued task, so a
  // response can land in the gap between the two. The attribute is the only
  // signal already current in that gap: a dialog whose open state changed
  // while the request ran belongs to an interaction the user has left.
  const wasOpen = dialog.open;

  const response = await fetch(url, {
    credentials: 'same-origin',
    // `ApplicationController#modal_layout_if_xhr` keys off `request.xhr?`.
    // Without this header Rails wraps the response in the full application
    // layout and the dialog fills with an entire page.
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });

  if (!response.ok) {
    throw new Error(`Modal request failed: ${response.status} ${url}`);
  }

  const contents = await response.text();
  if (revisions.get(dialog) !== revision) return;
  if (dialog.open !== wasOpen) return;

  dialog.innerHTML = contents;
  fetched.add(dialog);
  reportInertContent(dialog, url);
  openModal(dialog);
}

function reportInertContent(dialog: HTMLDialogElement, url: string): void {
  const inert = dialog.querySelector('script, [data-react-class]');
  if (!inert) return;
  // eslint-disable-next-line no-console
  console.error(
    `Modal content from ${url} needs a script, and fetched content never runs one:`,
    inert
  );
}

/**
 * A click on the backdrop reports the dialog itself as its target; a click on
 * anything inside reports that child. The dialog's own padding is inside its
 * box, so the pointer position is compared against the box rather than the
 * target alone.
 *
 * Only dialogs that declare `closedby="any"` take part, matching the opt-in
 * the attribute itself governs on browsers that support it — a dialog that
 * omits it stays modal-only regardless of which path handles it.
 */
function handleLightDismiss(event: MouseEvent): void {
  const dialog = event.target;
  if (!isDialog(dialog) || !dialog.open) return;
  if (dialog.getAttribute('closedby') !== 'any') return;
  if (pressedOn !== dialog) return;

  const box = dialog.getBoundingClientRect();
  // An unlaid-out dialog reports a zero box, which would read as "every click
  // is outside".
  if (box.width === 0 || box.height === 0) return;

  const outside = (x: number, y: number): boolean =>
    x < box.left || x > box.right || y < box.top || y > box.bottom;

  if (outside(event.clientX, event.clientY) && outside(pressX, pressY)) {
    dialog.close();
  }
}
